import React, { useState, useMemo } from "react";
import {
  Box,
  Button,
  HStack,
  Text,
  useColorModeValue,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  useColorMode,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import CompactUserCard from "./CompactUserCard";
import { User } from "./BurnoutAnalysisMain";
import { getThemeClasses } from "../../utils/themeUtils";

interface UserCardGridWithPaginationProps {
  users: User[];
  onSelectUser: (user: User) => void;
  organizationId: string;
}
 
const UserCardGridWithPagination: React.FC<UserCardGridWithPaginationProps> = ({
  users,
  onSelectUser,
  organizationId,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const itemsPerPage = 6;
  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";
  const theme = getThemeClasses(isDark);

  // Filter users based on search query
  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return users;
    
    const query = searchQuery.toLowerCase();
    return users.filter(user => 
      `${user.firstName} ${user.lastName}`.toLowerCase().includes(query) ||
      user.role.toLowerCase().includes(query)
    );
  }, [users, searchQuery]);

  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentUsers = filteredUsers.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Reset to first page when search query changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const bg = useColorModeValue("white", "#1A1C1F");
  const borderColor = useColorModeValue("gray.200", "#2E2F33");
  const headerBg = useColorModeValue("gray.50", "#14161B");

  return (
    <Box bg={bg} borderWidth="1px" borderColor={borderColor} shadow="sm" overflow="hidden">
      <Flex
        justify="space-between"
        align="center"
        p={4}
        borderBottomWidth="1px"
        borderColor={borderColor}
      >
        <InputGroup maxW="300px">
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.400" />
          </InputLeftElement>
          <Input
            placeholder="Search members..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="md"
            className={`${theme.searchInput} rounded-md`}
            focusBorderColor="#B59143"
            _focus={{ borderColor: '#B59143', boxShadow: '0 0 0 1px #B59143' }}
            _hover={{ borderColor: '#B59143' }}
          />
        </InputGroup>
        <Text fontSize="sm" color="gray.500">
          Showing {filteredUsers.length > 0 ? startIndex + 1 : 0}-{Math.min(endIndex, filteredUsers.length)} of {filteredUsers.length} members
        </Text>
      </Flex>

      <Box p={5} overflowX="auto">
        <Table variant="simple" size="sm" width="100%">
          <Thead position="sticky" top={0} zIndex={1} bg={headerBg}>
            <Tr>
              <Th py={"8px"} width="80px">Progress</Th>
              <Th>Name</Th>
              <Th>Role</Th>
              <Th textAlign={"center"}>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {currentUsers.map((user) => (
              <CompactUserCard
                key={user.id}
                user={user}
                onView={onSelectUser}
                organizationId={organizationId}
              />
            ))}
          </Tbody>
        </Table>
      </Box>

      {totalPages > 1 && (
        <Flex
          justify="space-between"
          align="center"
          p={4}
          borderTopWidth="1px"
          borderColor={borderColor}
        >
          <Text fontSize="sm" color="gray.500">
            Page {currentPage} of {totalPages}
          </Text>
          <HStack spacing={2}>
            <Button
              size="sm"
              onClick={() => handlePageChange(currentPage - 1)}
              isDisabled={currentPage === 1}
              variant="outline"
            >
              Previous
            </Button>
            <Button
              size="sm"
              onClick={() => handlePageChange(currentPage + 1)}
              isDisabled={currentPage === totalPages}
              variant="outline"
            >
              Next
            </Button>
          </HStack>
        </Flex>
      )}
    </Box>
  );
};

export default UserCardGridWithPagination;
